import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  LayoutDashboard,
  Mail,
  Users,
  FileText,
  CheckCircle,
  Settings,
  LogOut,
  Menu,
  X,
  MessageSquare,
  Bell,
  FolderOpen,
  Sparkles,
  FileEdit,
  FileImage,
  Palette,
  BookOpen,
  PenTool,
  LayoutTemplate,
  Calculator,
} from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, isManager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'لوحة التحكم', labelEn: 'Dashboard' },
    { path: '/emails', icon: Mail, label: 'صندوق الوارد', labelEn: 'Inbox' },
    { path: '/drafts', icon: FileEdit, label: 'المسودات', labelEn: 'Drafts' },
    { path: '/suppliers', icon: Users, label: 'الموردين', labelEn: 'Suppliers' },
    { path: '/quotes', icon: FileText, label: 'عروض الأسعار', labelEn: 'Quotes' },
    { path: '/approvals', icon: CheckCircle, label: 'الموافقات', labelEn: 'Approvals' },
    { path: '/submittals', icon: FolderOpen, label: 'Submittals', labelEn: 'Submittals' },
    { path: '/pdf-modifier', icon: FileImage, label: 'تعديل PDF', labelEn: 'PDF Modifier' },
    { path: '/design-studio', icon: Palette, label: 'استوديو التصميم', labelEn: 'Design Studio' },
    { path: '/cad-studio', icon: PenTool, label: 'استوديو CAD', labelEn: 'CAD Studio' },
    { path: '/template-studio', icon: LayoutTemplate, label: 'استوديو القوالب', labelEn: 'Template Studio' },
    { path: '/quantity-calculator', icon: Calculator, label: 'حساب الكميات', labelEn: 'Quantity Calculator' },
    { path: '/profile-generator', icon: BookOpen, label: 'منشئ البروفايل', labelEn: 'Profile Generator' },
    { path: '/chat', icon: MessageSquare, label: 'Nicole', labelEn: 'Assistant' },
    { path: '/ai-services', icon: Sparkles, label: 'خدمات AI', labelEn: 'AI Services' },
    { path: '/settings', icon: Settings, label: 'الإعدادات', labelEn: 'Settings' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="rounded-sm"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="font-heading text-xl font-bold text-primary uppercase tracking-tight">
            NICOLE
          </h1>
          <Button variant="ghost" size="icon" className="rounded-sm">
            <Bell className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-card border-r border-border">
          <div className="p-6 flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_smart-mail-bot/artifacts/zsz37awn_1.png" alt="DHR" className="h-10" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-primary uppercase tracking-tight">
                NICOLE
              </h1>
              <p className="text-xs text-muted-foreground">
                DHR Business Assistant
              </p>
            </div>
          </div>

          <nav className="flex-1 px-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary border-l-2 border-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                <item.icon className="h-5 w-5" />
                <div>
                  <span className="block text-sm font-medium">{item.label}</span>
                  <span className="block text-xs opacity-70">{item.labelEn}</span>
                </div>
              </Link>
            ))}
          </nav>

          <Separator className="mx-4" />

          <div className="p-4">
            <div className="p-3 rounded-sm bg-muted/30 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <span className="font-heading text-primary font-bold">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.role === 'manager' ? 'مدير' : 'موظف'}
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive rounded-sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              تسجيل الخروج
            </Button>
          </div>
        </aside>

        {/* Sidebar - Mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border animate-fade-in">
              <div className="p-4 flex items-center justify-between">
                <h1 className="font-heading text-xl font-bold text-primary uppercase">
                  NICOLE
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-sm"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <nav className="px-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                      isActive(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="text-sm">{item.label}</span>
                  </Link>
                ))}
              </nav>

              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground rounded-sm"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  تسجيل الخروج
                </Button>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          <div className="p-4 lg:p-8 max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
